/**
 * The two charts that answer a question.
 *
 * Hand-written SVG, and deliberately. There is no charting library in this
 * project, and adding one would bring a business-dashboard vocabulary — thick
 * gridlines, rounded bars, a legend nobody reads — into the one screen whose
 * job is to be believed. These use the same hairlines and tabular numerals as
 * everything else, and each draws exactly what its question needs.
 */

import { useState } from "react";

import type { CohortScan, CohortSubject, Distribution } from "@/ipc/contract";
import { cn } from "@/lib/utils";

/* ------------------------------------------------- completeness matrix --- */

type CellState = "match" | "differs" | "missing";

function cellState(subject: CohortSubject, role: string, common: string | null): CellState {
  const image = subject.images.find((i) => i.role === role);
  if (!image) return "missing";
  if (!common || !image.signature) return "match";
  const shape = `${image.signature.dim.join("×")}·${image.signature.voxelUm.join("×")}·${image.signature.orientation}·${image.signature.datatype}`;
  return shape === common ? "match" : "differs";
}

const CELL_CLASS: Record<CellState, string> = {
  match: "bg-emerald-500",
  differs: "bg-amber-500",
  missing: "bg-neutral-200",
};

const CELL_WORD: Record<CellState, string> = {
  match: "present, same geometry as the cohort",
  differs: "present, geometry differs from the cohort",
  missing: "missing",
};

/**
 * Subject × role, one cell each. The chart that earns its place: a column with
 * holes or a row that is nearly empty is visible without reading anything.
 */
export function CompletenessMatrix({
  scan,
  roles,
  onPick,
}: {
  scan: CohortScan;
  roles: string[];
  onPick?: (subjectId: string) => void;
}) {
  const [hover, setHover] = useState<string | null>(null);

  // The cohort's own geometry per role, as a comparable string.
  const common = new Map<string, string | null>(
    roles.map((role) => {
      const g = scan.consistency.find((c) => c.role === role)?.common ?? null;
      return [
        role,
        g ? `${g.dim.join("×")}·${g.voxelUm.join("×")}·${g.orientation}·${g.datatype}` : null,
      ];
    }),
  );

  const cell = 11;
  const gap = 2;
  const labelWidth = 116;
  const headerHeight = 54;
  const width = labelWidth + roles.length * (cell + gap);
  const height = headerHeight + scan.subjects.length * (cell + gap);

  return (
    <div className="overflow-auto">
      <svg width={width} height={height} role="img" aria-label="Completeness by subject and role">
        {roles.map((role, c) => (
          <text
            key={role}
            x={labelWidth + c * (cell + gap) + cell / 2}
            y={headerHeight - 8}
            transform={`rotate(-45 ${labelWidth + c * (cell + gap) + cell / 2} ${headerHeight - 8})`}
            className="fill-neutral-500 text-[9px]"
            textAnchor="start"
          >
            {role}
          </text>
        ))}

        {scan.subjects.map((subject, r) => {
          const y = headerHeight + r * (cell + gap);
          const isHover = hover === subject.subjectId;
          return (
            <g key={subject.subjectId} onMouseEnter={() => setHover(subject.subjectId)}>
              <text
                x={labelWidth - 6}
                y={y + cell - 2}
                textAnchor="end"
                className={cn(
                  "text-[9px] font-mono",
                  isHover ? "fill-neutral-900" : "fill-neutral-400",
                )}
                onClick={() => onPick?.(subject.subjectId)}
                style={{ cursor: onPick ? "pointer" : undefined }}
              >
                {subject.subjectId}
              </text>
              {roles.map((role, c) => {
                const state = cellState(subject, role, common.get(role) ?? null);
                return (
                  <rect
                    key={role}
                    x={labelWidth + c * (cell + gap)}
                    y={y}
                    width={cell}
                    height={cell}
                    rx={1.5}
                    className={CELL_CLASS[state]}
                    opacity={isHover ? 1 : 0.85}
                  >
                    <title>{`${subject.subjectId} · ${role}: ${CELL_WORD[state]}`}</title>
                  </rect>
                );
              })}
            </g>
          );
        })}
      </svg>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10.5px] text-neutral-500">
        {(["match", "differs", "missing"] as CellState[]).map((s) => (
          <span key={s} className="flex items-center gap-1.5">
            <span className={cn("h-2.5 w-2.5 rounded-sm", CELL_CLASS[s])} />
            {CELL_WORD[s]}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------- strip plot ------ */

function formatValue(value: number, unit: string): string {
  if (unit !== "bytes") return value.toLocaleString();
  if (value >= 1e9) return `${(value / 1e9).toFixed(1)} GB`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(1)} MB`;
  return `${(value / 1e3).toFixed(0)} kB`;
}

/**
 * One row per distribution: every subject a tick, the middle half a bar, the
 * median a line, and the outliers named underneath.
 *
 * A box plot rather than a histogram because the question is "who is unlike
 * the others", and a histogram loses which subject a bar belongs to — which is
 * the only part anybody can act on.
 */
export function StripPlot({ d }: { d: Distribution }) {
  const width = 560;
  const height = 34;
  const pad = 6;
  const span = d.max - d.min || 1;
  const x = (v: number) => pad + ((v - d.min) / span) * (width - pad * 2);
  const outlierIds = new Set(d.outliers.map((o) => o.subjectId));

  return (
    <div className="py-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[11.5px] font-medium text-neutral-800">{d.label}</span>
        <span className="text-[10.5px] tabular-nums text-neutral-500">
          median {formatValue(d.median, d.unit)} · {formatValue(d.min, d.unit)} –{" "}
          {formatValue(d.max, d.unit)}
        </span>
      </div>

      <svg width={width} height={height} className="mt-1 max-w-full" role="img" aria-label={d.label}>
        {/* the middle half */}
        <rect
          x={x(d.q1)}
          y={10}
          width={Math.max(x(d.q3) - x(d.q1), 1)}
          height={12}
          rx={2}
          className="fill-ads-accent-soft"
        />
        <line x1={pad} x2={width - pad} y1={16} y2={16} className="stroke-ads-border" strokeWidth={1} />
        <line
          x1={x(d.median)}
          x2={x(d.median)}
          y1={8}
          y2={24}
          className="stroke-ads-accent"
          strokeWidth={1.5}
        />
        {d.points.map((p) => {
          const out = outlierIds.has(p.subjectId);
          return (
            <circle
              key={p.subjectId}
              cx={x(p.value)}
              cy={16}
              r={out ? 3.5 : 2.5}
              className={out ? "fill-amber-500" : "fill-neutral-400"}
              opacity={out ? 1 : 0.7}
            >
              <title>{`${p.subjectId}: ${formatValue(p.value, d.unit)}`}</title>
            </circle>
          );
        })}
      </svg>

      {d.outliers.length ? (
        <p className="text-[10.5px] leading-relaxed text-amber-700">
          {d.outliers
            .slice(0, 6)
            .map((o) => `${o.subjectId} (${formatValue(o.value, d.unit)})`)
            .join(", ")}
          {d.outliers.length > 6 ? ` and ${d.outliers.length - 6} more` : ""}
        </p>
      ) : null}
    </div>
  );
}
